import {
	ForwardedRef,
	forwardRef,
	memo,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
} from "react";
import { View } from "react-native";
import { CoreListProps } from "../types";
import VirtualListFooter from "./components/VirtualListFooter";
import VirtualListHeader from "./components/VirtualListHeader";
import VirtualRow from "./components/VirtualRow";
import useCalculateCells from "./hooks/useCalculateCells";
import useRowData from "./hooks/useRowData";
import useScrollPosition from "./hooks/useScrollPosition";
import { createVirtualListStore } from "./stores/virtualListStore";
import { VirtualListHandler } from "./types";

const getRandomLetters = () => {
	return Math.random().toString(36).substring(7);
};

const VirtualList = <T extends unknown>(
	{
		ListHeaderComponent,
		ListFooterComponent,
		ListHeaderComponentStyle,
		ListFooterComponentStyle,
		data,
		renderItem,
		keyExtractor,
		numColumns = 1,
		onEndReached,
		onEndReachedThreshold,
		extraData,
		contentContainerStyle,
		onScroll,
	}: CoreListProps<T>,
	ref: ForwardedRef<VirtualListHandler>,
) => {
	const rowData = useRowData({ data, numColumns, keyExtractor });
	const store = useMemo(() => createVirtualListStore(), []);

	useScrollPosition({
		store,
		onEndReached,
		onEndReachedThreshold,
		onScroll,
	});

	const didRender = useRef(false);
	const seenKeyLookup = useRef<Record<string, boolean>>({});

	useImperativeHandle(ref, () => ({
		scrollToOffset: ({ offset, animated }) => {
			window.scrollTo(0, offset);
		},
	}));

	useCalculateCells(store);

	// Cleaning up old keys
	useEffect(() => {
		didRender.current = true;
		const rowKeys = new Set(rowData.map(({ rowKey }) => rowKey));
		const measurementStore = store.getState().measurementLookup;
		for (const key of Object.keys(seenKeyLookup.current)) {
			if (!rowKeys.has(key)) {
				delete seenKeyLookup.current[key];
				// Cleaning up measurement leaks
				// Intentionally not setting state here to avoid unnecessary re-renders
				delete measurementStore[key];
			}
		}
	}, [rowData, store]);

	return (
		<View
			key={extraData}
			style={[
				contentContainerStyle,
				{
					overflow: "hidden",
				},
			]}
		>
			<VirtualListHeader
				ListHeaderComponent={ListHeaderComponent}
				ListHeaderComponentStyle={ListHeaderComponentStyle}
			/>
			<View
				onLayout={(event) => {
					store.setState({
						topOffset: event.nativeEvent.layout.y,
					});
				}}
			>
				{(() => {
					// If a new key is inserted in the middle of the rows, then it remeasures all following rows
					let remeasurementKey: string | null = null;
					return (rowData ?? []).map(({ rowKey, rows }, index) => {
						const didSeeKey = seenKeyLookup.current[rowKey];
						if (!didSeeKey) {
							seenKeyLookup.current[rowKey] = true;
							if (didRender.current && !remeasurementKey) {
								remeasurementKey = getRandomLetters();
							}
						}
						return (
							<VirtualRow
								store={store}
								key={rowKey}
								rowKey={rowKey}
								renderItem={renderItem}
								numColumns={numColumns}
								rowIndex={index}
								rows={rows}
								remeasurementKey={remeasurementKey}
								// Improved time to first paint for first few items
								isCellDefault={index < 5 ? true : false}
							/>
						);
					});
				})()}
				{}
			</View>
			<VirtualListFooter
				ListFooterComponent={ListFooterComponent}
				ListFooterComponentStyle={ListFooterComponentStyle}
			/>
		</View>
	);
};
export default memo(forwardRef(VirtualList));
